import { Input } from '@atlas/ui-primitives'
import { type KeyboardEvent, useId, useRef } from 'react'
import { PlaceholderMenu } from './PlaceholderMenu'
import { usePlaceholderCompletion } from './usePlaceholderCompletion'

/**
 * An `each` variable's per-item template, completing over that variable's own
 * `itemFields`.
 *
 * The same `{{}}` mechanism as the prompt body against a different vocabulary: here
 * a placeholder refers to a field of one ITEM, not to a prompt variable. Completing
 * over the declared fields is what makes the relationship visible — the template and
 * the field list are two halves of one contract, and a template naming a field
 * nothing promised is refused on save.
 *
 * Anchored under the input rather than at the caret: this is one line, so the menu
 * cannot cover the text being typed.
 */
export function ItemTemplateInput({
	value,
	fields,
	ariaLabel,
	placeholder,
	isDisabled,
	onChange,
}: {
	value: string
	/** The item fields this template may reference. */
	fields: string[]
	ariaLabel: string
	placeholder?: string
	isDisabled?: boolean
	onChange: (next: string) => void
}) {
	const wrap = useRef<HTMLDivElement>(null)
	const menuId = useId()
	const completion = usePlaceholderCompletion(fields)

	const field = () => wrap.current?.querySelector('input') ?? null

	const accept = (key: string) => {
		const node = field()
		const caret = node?.selectionEnd ?? value.length
		const next = completion.accept(value, caret, key)

		onChange(next.text)
		requestAnimationFrame(() => node?.setSelectionRange(next.caret, next.caret))
	}

	const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (completion.matches.length === 0) return

		if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
			event.preventDefault()
			completion.move(event.key === 'ArrowDown' ? 1 : -1)
			return
		}

		if (event.key === 'Enter' || event.key === 'Tab') {
			event.preventDefault()
			accept(completion.matches[completion.active] as string)
			return
		}

		if (event.key === 'Escape') completion.dismiss()
	}

	return (
		<div ref={wrap} className="relative">
			<Input
				value={value}
				aria-label={ariaLabel}
				className="font-mono"
				disabled={isDisabled}
				{...(placeholder === undefined ? {} : { placeholder })}
				// Combobox semantics while suggestions are open — same reason as the body
				// editor: the keyboard handling was right, nothing announced it.
				role="combobox"
				aria-expanded={completion.matches.length > 0}
				aria-controls={completion.matches.length > 0 ? menuId : undefined}
				aria-activedescendant={completion.matches.length > 0 ? `${menuId}-option-${completion.active}` : undefined}
				aria-autocomplete="list"
				onKeyDown={onKeyDown}
				onBlur={() => completion.dismiss()}
				onChange={(e) => {
					const { value: next, selectionEnd } = e.target
					onChange(next)
					completion.sync(next, selectionEnd !== null && selectionEnd > 0 ? selectionEnd : next.length)
				}}
			/>

			<PlaceholderMenu
				id={menuId}
				matches={completion.matches}
				active={completion.active}
				position={{ top: '100%', left: 0 }}
				onAccept={accept}
			/>
		</div>
	)
}
